import { SpaceMessageId, SpaceMessageIdSchema } from "../id"
import { z } from "zod/v4"
import { DocumentSnapshotLike } from "../firestore"
import { CreatedAt, UpdatedAt } from "./system"
import { UserDenormalizedWithoutLook } from "./user"
import { OGP } from "./ogp"
import { validateUserFreeInputText } from "../user-free-input-text-constraint"
import { MediaImage } from "../common"


export const SpaceMessageKind = z.union([
  z.literal("text"),
  z.literal("emotion"),
  z.literal("image"),
  z.literal("video"),
  z.literal("join"),
  z.literal("firstJoin"),
  z.literal("added"),
  z.literal("removed"),
  z.literal("leave"),
  z.literal("power"),
])

export type SpaceMessageKind = z.infer<typeof SpaceMessageKind>

const SpaceMessageData = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("text"),
    value: z.string(),
    ogp: OGP.optional(),
  }),
  z.object({
    kind: z.literal("emotion"),
    value: z.string(),
  }),
  z.object({
    kind: z.literal("image"),
    value: z.string().optional().describe("画像のURL。transaction中に値がないことを許容するが、読み取り時は必ず値がある"),
    media: MediaImage.optional().describe("CDN variants 付き画像。value と同じ画像を指すが、variants で複数解像度の URL を保持する"),
  }),
  z.object({
    kind: z.literal("video"),
    value: z.string().optional().describe("動画のURL。transaction中に値がないことを許容するが、読み取り時は必ず値がある"),
  }),
  z.object({
    kind: z.literal("firstJoin"),
  }),
  z.object({
    kind: z.literal("join"),
  }),
  z.object({
    kind: z.literal("leave"),
  }),
  z.object({
    kind: z.literal("added"),
  }),
  z.object({
    kind: z.literal("removed"),
  }),
  z.object({
    kind: z.literal("power"),
  }),
])

export const SpaceMessageDocumentBody = z.intersection(
  SpaceMessageData,
  z.object({
    user: UserDenormalizedWithoutLook,
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
)

export type SpaceMessageDocumentBody = z.infer<typeof SpaceMessageDocumentBody>

export const SpaceMessageDocument = z.intersection(
  SpaceMessageDocumentBody,
  z.object({
    id: SpaceMessageIdSchema,
  })
)

export type SpaceMessageDocument = z.infer<typeof SpaceMessageDocument>

export class SpaceMessageDocuments {

  static readonly MessageMinLength = 1
  // See: https://www.notion.so/popopo-inc/V-6c8bbb9a8a6d44499e2808fb650fbd1d?pvs=4#e81cfb78ac67474b8d14319d45d4fa0d
  static readonly MessageMaxLength = 400
  static readonly ExpiresDays = 30

  static toDocumentData(doc: SpaceMessageDocument): object {
    return {
      ...doc,
      // id は document path に入るので入れない
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): SpaceMessageDocument | null {
    if (!doc) {
      return null
    }

    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: SpaceMessageId(doc.id),
      created_at: data["created_at"],
      updated_at: data["updated_at"],
      kind: data["kind"],
      value: data["value"],
      user: data["user"],
      ogp: data.ogp,
      media: data["media"],
    }
  }
}

export const CreateSpaceMessageRequest = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("text"),
    value: validateUserFreeInputText({
      minLength: SpaceMessageDocuments.MessageMinLength,
      maxLength: SpaceMessageDocuments.MessageMaxLength,
    })
  }),
  z.object({
    kind: z.literal("emotion"),
    value: validateUserFreeInputText({
      minLength: SpaceMessageDocuments.MessageMinLength,
      maxLength: SpaceMessageDocuments.MessageMaxLength,
    })
  }),
  z.object({ kind: z.literal("image") }),
  z.object({ kind: z.literal("video") }),
  z.object({ kind: z.literal("power") }),
])

export type CreateSpaceMessageRequest = z.infer<typeof CreateSpaceMessageRequest>

export type CreateSpaceMessageBody = {
  kind: "firstJoin" | "join" | "leave" | "added" | "removed"
} | CreateSpaceMessageRequest
